import React, {useEffect, useRef, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Container,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    Link as MuiLink,
    Paper,
    TextField,
    Typography
} from '@mui/material';
import {Link, useNavigate} from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import {saveUser} from '../helpers/crypto';
import {Constants} from '../components/Constants';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID as string;
const API_URL = Constants.SERVER_URL;

declare global { interface Window { google: any; } }

const RESEND_TIMEOUT = 60; // seconds

const RegisterPage: React.FC = () => {
    const navigate = useNavigate();

    // Step: 'form' | 'verify'
    const [step, setStep] = useState<'form' | 'verify'>('form');

    const [form, setForm] = useState({
        name: '', last_name: '', email: '', phone: '', password: '', confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Verification step
    const [code, setCode] = useState('');
    const [resendSeconds, setResendSeconds] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const updateField = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm(prev => ({ ...prev, [field]: e.target.value }));

    // Google OAuth
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => {
            window.google?.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                callback: handleGoogleResponse,
            });
            window.google?.accounts.id.renderButton(
                document.getElementById('google-signup-btn'),
                { theme: 'outline', size: 'large', width: '100%', text: 'signup_with', locale: 'uk' }
            );
        };
        document.body.appendChild(script);
        return () => { document.body.removeChild(script); };
    }, []);

    useEffect(() => {
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, []);

    const startResendTimer = () => {
        setResendSeconds(RESEND_TIMEOUT);
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setResendSeconds(s => {
                if (s <= 1) { clearInterval(timerRef.current!); return 0; }
                return s - 1;
            });
        }, 1000);
    };

    const handleGoogleResponse = async (response: any) => {
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}${Constants.GOOGLE_AUTH_PREFIX}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ credential: response.credential }),
            });
            const data = await res.json();
            if (data.success) { saveUser(data.user); navigate('/'); }
            else setError(data.error || 'Помилка реєстрації через Google');
        } catch { setError('Помилка з\'єднання з сервером'); }
        finally { setLoading(false); }
    };

    // Step 1: validate form and send verification code
    const handleSendCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!form.name || !form.last_name || !form.email || !form.password) {
            setError('Заповніть всі обов\'язкові поля'); return;
        }
        if (form.password.length < 6) {
            setError('Пароль має містити мінімум 6 символів'); return;
        }
        if (form.password !== form.confirmPassword) {
            setError('Паролі не співпадають'); return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API_URL}${Constants.SEND_VERIFICATION_PREFIX}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: form.name, last_name: form.last_name,
                    email: form.email, phone: form.phone, password: form.password,
                }),
            });
            const data = await res.json();
            if (data.success) {
                setStep('verify');
                setCode('');
                startResendTimer();
            } else {
                setError(data.error || 'Помилка відправки коду');
            }
        } catch { setError('Помилка з\'єднання з сервером'); }
        finally { setLoading(false); }
    };

    // Step 2: verify code and register
    const handleVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (code.length !== 6) { setError('Введіть 6-значний код'); return; }

        setLoading(true);
        try {
            const res = await fetch(`${API_URL}${Constants.VERIFY_REGISTER_PREFIX}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: form.email, code }),
            });
            const data = await res.json();
            if (data.success) {
                saveUser(data.user);
                navigate('/');
            } else {
                setError(data.error || 'Невірний код');
                if (data.error?.includes('сплив')) setStep('form');
            }
        } catch { setError('Помилка з\'єднання з сервером'); }
        finally { setLoading(false); }
    };

    // Resend code
    const handleResend = async () => {
        if (resendSeconds > 0) return;
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}${Constants.SEND_VERIFICATION_PREFIX}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: form.name, last_name: form.last_name,
                    email: form.email, phone: form.phone, password: form.password,
                }),
            });
            const data = await res.json();
            if (data.success) { setCode(''); startResendTimer(); }
            else setError(data.error || 'Помилка відправки коду');
        } catch { setError('Помилка з\'єднання з сервером'); }
        finally { setLoading(false); }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f7f9fc', display: 'flex', alignItems: 'center', py: 4 }}>
            <Container maxWidth="sm">
                <Paper elevation={0} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3, border: '1px solid #e0e6ed' }}>
                    {/* Logo */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Box component={Link} to="/" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}>
                            <LocalHospitalIcon sx={{ color: '#0077CC', fontSize: 32 }} />
                            <Typography sx={{ fontWeight: 700, fontSize: 22, color: '#1a1a2e' }}>{Constants.APP_LABEL}</Typography>
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, mt: 3, mb: 0.5 }}>
                            {step === 'form' ? 'Реєстрація' : 'Підтвердження email'}
                        </Typography>
                        <Typography sx={{ color: '#555' }}>
                            {step === 'form'
                                ? 'Створіть акаунт для оформлення замовлень'
                                : <>Код надіслано на <b>{form.email}</b></>
                            }
                        </Typography>
                    </Box>

                    {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

                    {/* ── STEP 1: Registration form ── */}
                    {step === 'form' && (
                        <>
                            <Box id="google-signup-btn" sx={{ mb: 3, display: 'flex', justifyContent: 'center' }} />
                            <Divider sx={{ mb: 3 }}>
                                <Typography sx={{ color: '#999', fontSize: 13 }}>або заповніть форму</Typography>
                            </Divider>

                            <Box component="form" onSubmit={handleSendCode}>
                                <Grid container spacing={2} sx={{ mb: 0.5 }}>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField fullWidth label="Ім'я *" value={form.name} onChange={updateField('name')}
                                            slotProps={{ input: { startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#999' }} /></InputAdornment> } }} />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField fullWidth label="Прізвище *" value={form.last_name} onChange={updateField('last_name')}
                                            slotProps={{ input: { startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#999' }} /></InputAdornment> } }} />
                                    </Grid>
                                </Grid>

                                <TextField fullWidth label="Email *" type="email" value={form.email} onChange={updateField('email')}
                                    sx={{ mb: 2.5, mt: 2 }}
                                    slotProps={{ input: { startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: '#999' }} /></InputAdornment> } }} />

                                <TextField fullWidth label="Телефон" value={form.phone} onChange={updateField('phone')}
                                    sx={{ mb: 2.5 }}
                                    slotProps={{ input: { startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: '#999' }} /></InputAdornment> } }} />

                                <Grid container spacing={2} sx={{ mb: 0.5 }}>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField fullWidth label="Пароль *" type={showPassword ? 'text' : 'password'}
                                            value={form.password} onChange={updateField('password')}
                                            slotProps={{ input: {
                                                startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#999' }} /></InputAdornment>,
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowPassword(s => !s)} edge="end">
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}} />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField fullWidth label="Підтвердіть пароль *" type={showPassword ? 'text' : 'password'}
                                            value={form.confirmPassword} onChange={updateField('confirmPassword')}
                                            slotProps={{ input: { startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#999' }} /></InputAdornment> } }} />
                                    </Grid>
                                </Grid>

                                <Button type="submit" variant="contained" fullWidth size="large" disabled={loading}
                                    sx={{ textTransform: 'none', borderRadius: 2, py: 1.5, mb: 2, mt: 2 }}>
                                    {loading ? 'Надсилаємо код...' : 'Надіслати код підтвердження'}
                                </Button>
                            </Box>
                        </>
                    )}

                    {/* ── STEP 2: Code verification ── */}
                    {step === 'verify' && (
                        <Box component="form" onSubmit={handleVerify}>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                Перевірте папку <b>Вхідні</b> (та <b>Спам</b>). Код дійсний 5 хвилин.
                            </Alert>

                            <TextField
                                fullWidth
                                label="6-значний код"
                                value={code}
                                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                sx={{ mb: 3 }}
                                placeholder="000000"
                                slotProps={{
                                    input: {
                                        sx: { fontSize: 28, letterSpacing: 8, textAlign: 'center' },
                                        startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#999' }} /></InputAdornment>
                                    },
                                    htmlInput: { inputMode: 'numeric', maxLength: 6 }
                                }}
                            />

                            <Button type="submit" variant="contained" fullWidth size="large" disabled={loading || code.length !== 6}
                                sx={{ textTransform: 'none', borderRadius: 2, py: 1.5, mb: 2 }}>
                                {loading ? 'Перевіряємо...' : 'Підтвердити та зареєструватися'}
                            </Button>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Button size="small" sx={{ textTransform: 'none', color: '#555' }}
                                    onClick={() => { setStep('form'); setError(''); }}>
                                    ← Змінити дані
                                </Button>
                                <Button size="small" disabled={resendSeconds > 0 || loading}
                                    onClick={handleResend}
                                    sx={{ textTransform: 'none', color: resendSeconds > 0 ? '#aaa' : '#0077CC' }}>
                                    {resendSeconds > 0 ? `Надіслати повторно (${resendSeconds}с)` : 'Надіслати повторно'}
                                </Button>
                            </Box>
                        </Box>
                    )}

                    <Divider sx={{ my: 3 }} />
                    <Typography sx={{ textAlign: 'center', color: '#555', fontSize: 14 }}>
                        Вже маєте акаунт?{' '}
                        <MuiLink component={Link} to="/login" underline="hover" sx={{ fontWeight: 600 }}>
                            Увійти
                        </MuiLink>
                    </Typography>
                </Paper>
            </Container>
        </Box>
    );
};

export default RegisterPage;
